import type { JSX } from 'react';
import type { Cosmetic } from '@duck/contracts';

/**
 * A small procedural PREVIEW of a cosmetic (M6), drawn as inline SVG so it's crisp, cheap, and in the
 * a11y tree's control — NOT a Pixi canvas (which would mean spinning up a renderer per card). It mirrors
 * the in-world procedural shapes closely enough to recognise the item before buying. Decorative: the
 * item's NAME/price/description are conveyed by the surrounding card text, so the SVG is aria-hidden.
 *
 * Worn items (hat/glasses/outfit) draw a tiny duck head wearing the piece; pets draw the companion
 * alone. One SVG, switch on `cosmetic.style` — the same key the engine renders.
 */
export function CosmeticPreview({ cosmetic }: { cosmetic: Cosmetic }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 48 48"
      width={48}
      height={48}
      className="shrink-0 rounded-lg bg-black/30"
    >
      {cosmetic.slot === 'pet' ? <PetGlyph style={cosmetic.style} /> : <WornGlyph cosmetic={cosmetic} />}
    </svg>
  );
}

/** A small duck head (centred ~ (24,28)) wearing the hat/glasses/outfit being previewed. */
function WornGlyph({ cosmetic }: { cosmetic: Cosmetic }): JSX.Element {
  return (
    <g>
      {/* Outfit sits lowest (behind the head). */}
      {cosmetic.slot === 'outfit' && <OutfitGlyph style={cosmetic.style} />}
      {/* Duck head + eye + beak (facing right), centred. */}
      <circle cx={22} cy={28} r={11} fill="#ffd23f" />
      <circle cx={26} cy={25} r={2} fill="#141821" />
      <polygon points="32,28 41,30 32,33" fill="#ff9f1c" />
      {cosmetic.slot === 'glasses' && <GlassesGlyph style={cosmetic.style} />}
      {cosmetic.slot === 'hat' && <HatGlyph style={cosmetic.style} />}
    </g>
  );
}

function HatGlyph({ style }: { style: string }): JSX.Element | null {
  switch (style) {
    case 'beanie':
      return (
        <g>
          <ellipse cx={22} cy={18} rx={12} ry={8} fill="#e05a5a" />
          <rect x={10} y={18} width={24} height={5} rx={2.5} fill="#c94545" />
          <circle cx={22} cy={9} r={3} fill="#f4f4f8" />
        </g>
      );
    case 'party':
      return (
        <g>
          <polygon points="22,4 12,20 32,20" fill="#4dd0e1" />
          <circle cx={22} cy={4} r={3} fill="#ff7ab6" />
        </g>
      );
    case 'tophat':
      return (
        <g>
          <ellipse cx={22} cy={19} rx={15} ry={3.5} fill="#2a2f3a" />
          <rect x={13} y={3} width={18} height={17} rx={2} fill="#1c2129" />
          <rect x={13} y={14} width={18} height={3.5} fill="#b5793a" />
        </g>
      );
    case 'crown':
      return (
        <g>
          <polygon
            points="10,20 10,10 16,15 22,5 28,15 34,10 34,20"
            fill="#ffd23f"
            stroke="#caa400"
            strokeWidth={0.5}
          />
          <circle cx={22} cy={12} r={1.8} fill="#ff5d8f" />
          <circle cx={14} cy={14} r={1.4} fill="#5ad1ff" />
          <circle cx={30} cy={14} r={1.4} fill="#5ad1ff" />
        </g>
      );
    case 'cowboy':
      return (
        <g>
          <ellipse cx={22} cy={20} rx={17} ry={4} fill="#9c6b3f" />
          <rect x={13} y={6} width={18} height={14} rx={5} fill="#b07d4d" />
          <rect x={13} y={15} width={18} height={3} fill="#5e3d22" />
        </g>
      );
    case 'propeller':
      return (
        <g>
          <ellipse cx={22} cy={19} rx={12} ry={8} fill="#4f7cff" />
          <rect x={10} y={19} width={24} height={4} rx={2} fill="#ffd23f" />
          <line x1={22} y1={11} x2={22} y2={6} stroke="#2a2f3a" strokeWidth={1.4} />
          <ellipse cx={15} cy={5} rx={6} ry={2} fill="#ff5d8f" />
          <ellipse cx={29} cy={5} rx={6} ry={2} fill="#5ad1ff" />
          <circle cx={22} cy={5} r={1.6} fill="#2a2f3a" />
        </g>
      );
    case 'chef':
      return (
        <g fill="#f4f4f8">
          <rect x={11} y={14} width={22} height={7} rx={2} />
          <circle cx={15} cy={9} r={6} />
          <circle cx={22} cy={7} r={7} />
          <circle cx={29} cy={9} r={6} />
        </g>
      );
    case 'wizard':
      return (
        <g>
          <ellipse cx={22} cy={20} rx={15} ry={4} fill="#3a2d6b" />
          <polygon points="11,19 33,19 28,2" fill="#4b3a8c" />
          <polygon
            points="24,6 25.5,9 29,9 26.5,11.5 27.5,15 24,13 20.5,15 21.5,11.5 19,9 22.5,9"
            fill="#ffd23f"
          />
        </g>
      );
    case 'headphones':
      return (
        <g>
          <path d="M10 20 Q10 6 34 6 Q34 6 34 20" fill="none" stroke="#2a2f3a" strokeWidth={3} />
          <rect x={7} y={17} width={6} height={9} rx={2.5} fill="#16181f" />
          <rect x={31} y={17} width={6} height={9} rx={2.5} fill="#2a2f3a" />
          <rect x={32} y={19} width={3} height={5} rx={1.5} fill="#5ad1ff" />
        </g>
      );
    case 'flower':
      return (
        <g>
          {[
            [10, 18, '#ff7ab6'],
            [16, 13, '#ffd23f'],
            [23, 11, '#ff5d8f'],
            [30, 13, '#ffe08a'],
            [35, 18, '#ff9ecb'],
          ].map(([fx, fy, col], i) => (
            <g key={i}>
              {[0, 1, 2, 3, 4].map((p) => (
                <circle
                  key={p}
                  cx={(fx as number) + Math.cos((p / 5) * Math.PI * 2) * 2.6}
                  cy={(fy as number) + Math.sin((p / 5) * Math.PI * 2) * 2.6}
                  r={1.8}
                  fill={col as string}
                />
              ))}
              <circle cx={fx as number} cy={fy as number} r={1.5} fill="#fff3c4" />
            </g>
          ))}
        </g>
      );
    case 'pirate':
      return (
        <g>
          <ellipse cx={22} cy={17} rx={16} ry={6} fill="#1c2129" />
          <polygon points="6,17 22,3 38,17 22,12" fill="#2a2f3a" />
          <circle cx={22} cy={10} r={2.6} fill="#f4f4f8" />
          <circle cx={21} cy={9.5} r={0.7} fill="#1c2129" />
          <circle cx={23} cy={9.5} r={0.7} fill="#1c2129" />
        </g>
      );
    case 'halo':
      return (
        <g fill="none">
          <ellipse cx={22} cy={9} rx={12} ry={4} stroke="#ffd23f" strokeWidth={3} />
          <ellipse cx={22} cy={9} rx={12} ry={4} stroke="#fff3c4" strokeWidth={1} />
        </g>
      );
    default:
      return null;
  }
}

function GlassesGlyph({ style }: { style: string }): JSX.Element | null {
  switch (style) {
    case 'round':
      return (
        <g fill="rgba(191,232,255,0.4)" stroke="#2a2f3a" strokeWidth={1.6}>
          <circle cx={26} cy={26} r={5} />
          <circle cx={15} cy={26} r={5} />
          <line x1={20} y1={26} x2={21} y2={26} />
        </g>
      );
    case 'shades':
      return (
        <g fill="#16181f">
          <rect x={21} y={22} width={11} height={7} rx={2} />
          <rect x={9} y={22} width={11} height={7} rx={2} />
          <rect x={20} y={24} width={2} height={2} />
        </g>
      );
    case 'heart':
      return (
        <g fill="rgba(255,93,143,0.9)">
          <path d="M26 31 L20 25 Q20 21 23 22 Q24 23 26 25 Q28 23 29 22 Q32 21 32 25 Z" />
          <path d="M15 31 L9 25 Q9 21 12 22 Q13 23 15 25 Q17 23 18 22 Q21 21 21 25 Z" />
        </g>
      );
    case 'monocle':
      return (
        <g>
          <circle cx={26} cy={26} r={5.5} fill="rgba(255,255,255,0.18)" stroke="#ffd23f" strokeWidth={1.8} />
          <path d="M24 31 Q21 35 20 40" fill="none" stroke="#ffd23f" strokeWidth={1} />
        </g>
      );
    case '3d':
      return (
        <g stroke="#f4f4f8" strokeWidth={1}>
          <rect x={21} y={22} width={11} height={7} rx={1.5} fill="rgba(255,59,80,0.6)" />
          <rect x={9} y={22} width={11} height={7} rx={1.5} fill="rgba(43,209,255,0.6)" />
        </g>
      );
    case 'goggles':
      return (
        <g>
          <rect x={8} y={21} width={26} height={10} rx={5} fill="#2a2f3a" />
          <rect x={11} y={23} width={20} height={6} rx={3} fill="rgba(90,209,255,0.5)" />
          <rect x={11} y={23} width={20} height={2} rx={1} fill="rgba(244,244,248,0.35)" />
        </g>
      );
    case 'star':
      return (
        <g fill="#16181f">
          <polygon points="26,21 27.4,24.4 31,24.4 28.1,26.6 29.2,30 26,27.9 22.8,30 23.9,26.6 21,24.4 24.6,24.4" />
          <polygon points="15,21 16.4,24.4 20,24.4 17.1,26.6 18.2,30 15,27.9 11.8,30 12.9,26.6 10,24.4 13.6,24.4" />
        </g>
      );
    case 'nerd':
      return (
        <g fill="rgba(191,232,255,0.25)" stroke="#16181f" strokeWidth={2}>
          <rect x={21} y={22} width={11} height={9} rx={1.5} />
          <rect x={9} y={22} width={11} height={9} rx={1.5} />
          <rect x={20} y={25} width={2} height={4} fill="#f4f4f8" stroke="none" />
        </g>
      );
    default:
      return null;
  }
}

function OutfitGlyph({ style }: { style: string }): JSX.Element | null {
  switch (style) {
    case 'scarf':
      return (
        <g>
          <ellipse cx={22} cy={37} rx={15} ry={5} fill="#e06d3a" />
          <rect x={19} y={37} width={6} height={9} rx={3} fill="#c95a2b" />
        </g>
      );
    case 'hoodie':
      return (
        <g>
          <ellipse cx={22} cy={40} rx={17} ry={8} fill="#4f7cff" />
          <ellipse cx={22} cy={20} rx={9} ry={6} fill="#3a63d8" />
        </g>
      );
    case 'cape':
      return (
        <g>
          <polygon points="22,18 6,30 12,46 22,42" fill="#9b2c5d" />
          <circle cx={22} cy={20} r={2.4} fill="#ffd23f" />
        </g>
      );
    case 'tuxedo':
      return (
        <g>
          <ellipse cx={22} cy={40} rx={17} ry={9} fill="#1c2129" />
          <polygon points="18,32 26,32 24,48 20,48" fill="#f4f4f8" />
          <polygon points="18,32 22,38 14,42" fill="#10131a" />
          <polygon points="26,32 22,38 30,42" fill="#10131a" />
          <polygon points="18,32 22,35 18,38" fill="#2a2f3a" />
          <polygon points="26,32 22,35 26,38" fill="#2a2f3a" />
        </g>
      );
    case 'hivis':
      return (
        <g>
          <ellipse cx={22} cy={40} rx={17} ry={9} fill="#ffaa00" />
          <rect x={11} y={34} width={4} height={13} rx={1} fill="rgba(244,244,248,0.9)" />
          <rect x={29} y={34} width={4} height={13} rx={1} fill="rgba(244,244,248,0.9)" />
          <rect x={11} y={39} width={22} height={3} rx={1} fill="rgba(244,244,248,0.9)" />
        </g>
      );
    case 'overalls':
      return (
        <g>
          <ellipse cx={22} cy={42} rx={16} ry={8} fill="#4a6fa5" />
          <rect x={15} y={32} width={14} height={12} rx={2} fill="#3c5d8a" />
          <rect x={16} y={26} width={2.5} height={9} rx={1} fill="#3c5d8a" />
          <rect x={25.5} y={26} width={2.5} height={9} rx={1} fill="#3c5d8a" />
          <circle cx={17} cy={34} r={1.2} fill="#ffd23f" />
          <circle cx={27} cy={34} r={1.2} fill="#ffd23f" />
        </g>
      );
    case 'labcoat':
      return (
        <g>
          <ellipse cx={22} cy={41} rx={17} ry={9} fill="#f4f4f8" />
          <polygon points="20,32 12,40 15,42" fill="#e6e6ee" />
          <polygon points="24,32 32,40 29,42" fill="#e6e6ee" />
          <line x1={22} y1={34} x2={22} y2={48} stroke="#d0d0da" strokeWidth={1} />
          <rect x={11} y={40} width={6} height={5} rx={1} fill="#eef0f6" stroke="#d0d0da" strokeWidth={0.8} />
          <rect x={13} y={38} width={1.2} height={5} fill="#5ad1ff" />
        </g>
      );
    case 'raincoat':
      return (
        <g>
          <ellipse cx={22} cy={41} rx={17} ry={9} fill="#ffd23f" />
          <ellipse cx={22} cy={26} rx={9} ry={6} fill="#f0bf2a" />
          <line x1={22} y1={34} x2={22} y2={48} stroke="#d9a400" strokeWidth={1} />
          <circle cx={22} cy={38} r={1} fill="#b98a00" />
          <circle cx={22} cy={44} r={1} fill="#b98a00" />
        </g>
      );
    case 'galaxy':
      return (
        <g>
          <ellipse cx={22} cy={40} rx={17} ry={9} fill="#1b1245" />
          <ellipse cx={18} cy={38} rx={11} ry={7} fill="rgba(90,63,176,0.5)" />
          <ellipse cx={28} cy={44} rx={8} ry={5} fill="rgba(176,63,140,0.35)" />
          {[
            [10, 34],
            [19, 46],
            [30, 33],
            [35, 43],
            [24, 37],
            [14, 47],
          ].map(([sx, sy], i) => (
            <circle key={i} cx={sx} cy={sy} r={1} fill="#f4f4f8" />
          ))}
          <polygon points="26,30 27,32 29,32 27.5,33.5 28,36 26,34.5 24,36 24.5,33.5 23,32 25,32" fill="#ffd23f" />
        </g>
      );
    case 'neon':
      return (
        <g>
          <ellipse cx={22} cy={40} rx={17} ry={9} fill="#10142a" />
          <polyline points="7,38 16,38 20,44 32,44" fill="none" stroke="#2bffd1" strokeWidth={1.6} />
          <polyline points="11,46 22,46 26,40 37,40" fill="none" stroke="#ff3bd1" strokeWidth={1.6} />
          <circle cx={16} cy={38} r={1.6} fill="#2bffd1" />
          <circle cx={26} cy={40} r={1.6} fill="#ff3bd1" />
        </g>
      );
    case 'robot':
      return (
        <g>
          <ellipse cx={22} cy={40} rx={17} ry={9} fill="#9aa3b2" />
          <ellipse cx={17} cy={36} rx={11} ry={6} fill="rgba(194,201,212,0.7)" />
          <line x1={5} y1={42} x2={39} y2={42} stroke="#6b7382" strokeWidth={1} />
          <line x1={22} y1={32} x2={22} y2={48} stroke="#6b7382" strokeWidth={1} />
          <rect x={18} y={38} width={8} height={6} rx={1} fill="#2a2f3a" />
          <circle cx={22} cy={41} r={1.8} fill="#5ad1ff" />
        </g>
      );
    case 'gold':
      return (
        <g>
          <ellipse cx={22} cy={40} rx={17} ry={9} fill="#e8a900" />
          <ellipse cx={19} cy={37} rx={13} ry={7} fill="rgba(255,210,63,0.85)" />
          <ellipse cx={15} cy={34} rx={6} ry={4} fill="rgba(255,243,196,0.8)" />
          <polygon points="31,32 32,34 34,34 32.5,35.5 33,38 31,36.5 29,38 29.5,35.5 28,34 30,34" fill="#f4f4f8" />
        </g>
      );
    default:
      return null;
  }
}

/** A companion pet drawn alone (centred), matching the in-world style. */
function PetGlyph({ style }: { style: string }): JSX.Element | null {
  switch (style) {
    case 'duckling':
      return (
        <g>
          <ellipse cx={24} cy={32} rx={10} ry={8} fill="#ffd23f" />
          <circle cx={31} cy={22} r={6} fill="#ffd23f" />
          <circle cx={33} cy={20} r={1.4} fill="#141821" />
          <polygon points="37,22 43,24 37,26" fill="#ff9f1c" />
        </g>
      );
    case 'cat':
      return (
        <g fill="#8a8f9c">
          <ellipse cx={22} cy={32} rx={11} ry={8} />
          <circle cx={30} cy={22} r={6.5} />
          <polygon points="26,18 28,10 31,18" />
          <polygon points="31,18 34,10 37,18" />
          <circle cx={32} cy={22} r={1.3} fill="#2bd17a" />
          <line x1={10} y1={30} x2={4} y2={22} stroke="#8a8f9c" strokeWidth={2.4} />
        </g>
      );
    case 'bot':
      return (
        <g>
          <rect x={15} y={20} width={18} height={18} rx={4} fill="#5ad1ff" />
          <rect x={18} y={25} width={12} height={6} rx={1.5} fill="#102030" />
          <circle cx={22} cy={28} r={1.4} fill="#9bf0ff" />
          <circle cx={27} cy={28} r={1.4} fill="#9bf0ff" />
          <line x1={24} y1={20} x2={24} y2={13} stroke="#9bf0ff" strokeWidth={1.6} />
          <circle cx={24} cy={12} r={2.2} fill="#ff5d8f" />
        </g>
      );
    case 'crab':
      return (
        <g>
          <ellipse cx={24} cy={28} rx={10} ry={7} fill="#e0533a" />
          <ellipse cx={24} cy={25} rx={7} ry={3} fill="rgba(255,122,92,0.6)" />
          <line x1={20} y1={22} x2={20} y2={16} stroke="#e0533a" strokeWidth={1.6} />
          <line x1={28} y1={22} x2={28} y2={16} stroke="#e0533a" strokeWidth={1.6} />
          <circle cx={20} cy={15} r={1.6} fill="#f4f4f8" />
          <circle cx={28} cy={15} r={1.6} fill="#f4f4f8" />
          <circle cx={20} cy={15} r={0.8} fill="#141821" />
          <circle cx={28} cy={15} r={0.8} fill="#141821" />
          <polygon points="14,27 8,23 11,29 8,29" fill="#e0533a" />
          <polygon points="34,27 40,23 37,29 40,29" fill="#e0533a" />
        </g>
      );
    case 'frog':
      return (
        <g>
          <ellipse cx={24} cy={30} rx={10} ry={7} fill="#4caf50" />
          <ellipse cx={24} cy={28} rx={7} ry={3} fill="rgba(111,210,117,0.5)" />
          <circle cx={19} cy={20} r={3.6} fill="#4caf50" />
          <circle cx={29} cy={20} r={3.6} fill="#4caf50" />
          <circle cx={19} cy={19.5} r={1.8} fill="#f4f4f8" />
          <circle cx={29} cy={19.5} r={1.8} fill="#f4f4f8" />
          <circle cx={19} cy={19.5} r={0.9} fill="#141821" />
          <circle cx={29} cy={19.5} r={0.9} fill="#141821" />
          <path d="M18 29 Q24 33 30 29" fill="none" stroke="#2e7d32" strokeWidth={1.3} />
        </g>
      );
    case 'butterfly':
      return (
        <g>
          <ellipse cx={17} cy={22} rx={6} ry={7} fill="rgba(255,122,182,0.9)" />
          <ellipse cx={31} cy={22} rx={6} ry={7} fill="rgba(255,122,182,0.9)" />
          <ellipse cx={18} cy={30} rx={5} ry={5} fill="rgba(90,209,255,0.9)" />
          <ellipse cx={30} cy={30} rx={5} ry={5} fill="rgba(90,209,255,0.9)" />
          <circle cx={17} cy={22} r={1.8} fill="rgba(244,244,248,0.7)" />
          <circle cx={31} cy={22} r={1.8} fill="rgba(244,244,248,0.7)" />
          <rect x={23} y={18} width={2} height={16} rx={1} fill="#2a2f3a" />
          <line x1={23.5} y1={18} x2={20} y2={13} stroke="#2a2f3a" strokeWidth={1} />
          <line x1={24.5} y1={18} x2={28} y2={13} stroke="#2a2f3a" strokeWidth={1} />
        </g>
      );
    case 'ghost':
      return (
        <g>
          <path
            d="M15 36 L15 22 Q15 14 24 14 Q33 14 33 22 L33 36 L29 32 L25.5 36 L21.5 32 L18 36 Z"
            fill="rgba(244,244,248,0.88)"
          />
          <circle cx={20} cy={23} r={1.8} fill="#3a3f4a" />
          <circle cx={28} cy={23} r={1.8} fill="#3a3f4a" />
          <ellipse cx={24} cy={28} rx={2.4} ry={1.6} fill="rgba(255,158,203,0.6)" />
        </g>
      );
    default:
      return null;
  }
}
